import React from 'react';
import adminApi from '../../../services/adminApi';
import styles from './AdminCampaigns.module.css';

const TABS = [
    { key: 'desktop', label: 'Desktop' },
    { key: 'mobile', label: 'Mobile' },
    { key: 'text', label: 'Plain text' },
];

const ROLE_LABELS = { cta: 'Button', hero: 'Hero image', link: 'Link', unsubscribe: 'Unsubscribe', preferences: 'Email preferences', compliance: 'Compliance' };

/** Destination URLs as they will be delivered (attribution tags hidden). Format-checked only. */
function PreviewLinks({ validation }) {
    const { summary, links } = validation;
    const destinations = links.filter((l) => l.promotional);
    return (
        <div style={{ marginTop: 8 }} data-testid="preview-links">
            <div>
                <strong>Button:</strong>{' '}
                {summary.ctaText && summary.ctaUrl
                    ? <>{summary.ctaText} → <span className={styles.urlText}>{summary.ctaUrl}</span></>
                    : <span className={styles.checkWarn}>No button</span>}
            </div>
            {destinations.length === 0 ? (
                <div className={styles.checkError}><strong>No clickable destination links.</strong></div>
            ) : (
                <ul className={styles.linkList}>
                    {destinations.map((l, i) => (
                        <li key={i} className={l.ok ? '' : styles.checkError}>
                            {ROLE_LABELS[l.role] || 'Link'} “{l.label}” → <span className={styles.urlText}>{l.url || '(empty)'}</span>
                            {!l.ok && l.reason && <> — {l.reason}</>}
                        </li>
                    ))}
                </ul>
            )}
            <div className={styles.muted}>Link formats are checked; the pages themselves are not visited.</div>
        </div>
    );
}

/**
 * In-admin preview of the server-rendered campaign (the same renderer the
 * real send uses). The HTML is shown in a fully sandboxed iframe — no
 * scripts, no same-origin access — so campaign content can never execute in
 * the admin app.
 *
 * @param {{ campaignId?: string, overrides?: object, onClose: () => void }} props
 */
export default function CampaignPreviewModal({ campaignId, overrides, onClose }) {
    const [tab, setTab] = React.useState('desktop');
    const [preview, setPreview] = React.useState(null);
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        let cancelled = false;
        adminApi.previewCampaign(campaignId, overrides || {})
            .then((data) => { if (!cancelled) setPreview(data); })
            .catch((err) => { if (!cancelled) setError(err.message || 'Preview could not be rendered.'); });
        return () => { cancelled = true; };
    }, [campaignId, overrides]);

    React.useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div
                className={`${styles.modal} ${styles.modalWide}`}
                role="dialog"
                aria-modal="true"
                aria-label="Campaign preview"
                onClick={(e) => e.stopPropagation()}
            >
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>Preview</h2>
                    <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close preview">×</button>
                </div>

                {error && <div className={styles.errorBanner}>{error}</div>}
                {!preview && !error && <div className={styles.loading}>Rendering…</div>}

                {preview && (
                    <>
                        <div className={styles.previewSubject}>
                            <div><strong>Subject:</strong> {preview.subject}</div>
                            {preview.preheader && <div className={styles.muted}><strong>Preview text:</strong> {preview.preheader}</div>}
                            <div className={styles.muted}>Merge fields are shown with your own account details.</div>
                            {preview.validation && <PreviewLinks validation={preview.validation} />}
                        </div>
                        <div className={styles.tabs} role="tablist">
                            {TABS.map((t) => (
                                <button
                                    key={t.key}
                                    type="button"
                                    role="tab"
                                    aria-selected={tab === t.key}
                                    className={`${styles.tab} ${tab === t.key ? styles.tabActive : ''}`}
                                    onClick={() => setTab(t.key)}
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>
                        {tab === 'text' ? (
                            <pre className={styles.previewText} data-testid="preview-text">{preview.text}</pre>
                        ) : (
                            <div className={styles.previewStage}>
                                <iframe
                                    title={`Email preview (${tab})`}
                                    sandbox=""
                                    srcDoc={preview.html}
                                    className={`${styles.previewFrame} ${tab === 'mobile' ? styles.previewFrameMobile : styles.previewFrameDesktop}`}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
